import React, { useEffect, useState, useRef, useCallback } from "react";
import Navbar from "../../Component/LandingV2/Navbar";
import "./NiftyStatergy.scss";
import BacktestingDataDummy from "../../data/nifty/backtesting_o2c.json";
import NiftyNewsDummy from "../../data/nifty/news_data.json";
import Table from "./BackTestingTable";
import "./BackTestingTable.scss";
import SubNavbar from "../../Component/LandingV2/SubNavbar";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";

// Type assertions for react-icons compatibility
const ArrowRight = FaArrowRight as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const ArrowLeft = FaArrowLeft as React.ComponentType<React.SVGProps<SVGSVGElement>>;

// Types
export type BacktestingData = {
  Backtesting?: Backtesting | null;
  "About Strategy"?: AboutStrategy | null;
};

export type AboutStrategy = {
  Text: string;
};

export type Backtesting = {
  [key: string]: string;
};
export type HistoricalDataItem = {
  Date: string;
  Prediction: number;
  Confidence: number;
  label: number;
};

export type HistoricalData = HistoricalDataItem[];

export type NiftyNews = {
  Nifty: NiftyNewsItem[];
  NSE: NiftyNewsItem[];
  BSE: NiftyNewsItem[];
  Sensex: NiftyNewsItem[];
};

export type NiftyNewsItem = {
  title: string;
  published: string;
  source: {
    href: string;
    title: string;
  };
};

const chartDataUrls: { [key: string]: string } = {
  priceChart1:
    "https://atsstoragefiles.blob.core.windows.net/options-plots/CE_interactive_plot.html",
  priceChart2:
    "https://atsstoragefiles.blob.core.windows.net/options-plots/PE_interactive_plot.html",
  volumeChart:
    "https://atsstoragefiles.blob.core.windows.net/options-plots/Option_volume_and_OI_plot.html",
};

const NiftyStatergy = () => {
  const today = new Date().toISOString().split("T")[0];
  // const [showtext, setShowtext] = useState(false);
  // const [isOpen, setIsOpen] = useState(false);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [hasPredictionImage, setHasPredictionImage] = useState(true);
  // const [chartActiveTab, setChartActiveTab] = useState("tab1");
  // const [chartLoading, setChartLoading] = useState(true);

  // const iconHandler = () => {
  //   setShowtext(!showtext);
  //   setIsOpen(!isOpen);
  // };

  const [newsPaneWidth, setNewsPaneWidth] = useState(420);
  const isDragging = useRef(false);
  const splitRef = useRef<HTMLDivElement>(null);

  const onDragHandleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current || !splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const newWidth = rect.right - ev.clientX;
      setNewsPaneWidth(Math.min(Math.max(newWidth, 280), 700));
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  const [backtestingData, setBacktestingData] = useState<BacktestingData>({
    Backtesting: BacktestingDataDummy[0]["Backtesting"],
    "About Strategy": BacktestingDataDummy[1]["About Strategy"],
  });

  const [historicalData, setHistoricalData] = useState<HistoricalData>([]);

  const [newsData, setNewsData] = useState<NiftyNews>({
    Nifty: NiftyNewsDummy["Nifty"],
    NSE: NiftyNewsDummy["NSE"],
    BSE: NiftyNewsDummy["BSE"],
    Sensex: NiftyNewsDummy["Sensex"],
  });

  const fetchHistoricalData = async () => {
    try {
      const response = await fetch(
        process.env.REACT_APP_COMPANY_DOC +
          "/nifty_o2c_strategy/last_n_days_predictions.json"
      );
      const data = await response.json();
      data.sort(
        (a: HistoricalDataItem, b: HistoricalDataItem) =>
          new Date(b.Date).getTime() - new Date(a.Date).getTime()
      );
      setHistoricalData(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchNewsData = async () => {
    try {
      const response = await fetch(
        process.env.REACT_APP_COMPANY_DOC + "/nifty_o2c_strategy/news.json"
      );
      const data = await response.json();
      setNewsData(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchHistoricalData();
    fetchNewsData();
  }, []);

  const handleImageError = () => {
    setHasPredictionImage(false);
  };

  const [isMobile, setIsMobile] = useState<boolean>(false);

  const handleResize = () => {
    setIsMobile(window.innerWidth < 769);
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const [isNewsPaneVisible, setIsNewsPaneVisible] = useState<boolean>(false);
  const [newsSortOrder, setNewsSortOrder] = useState<"latest" | "oldest">("latest");
  const [newsSortDropdownOpen, setNewsSortDropdownOpen] = useState(false);
  const [histSortConfig, setHistSortConfig] = useState<{
    key: "Date" | "Prediction" | "Confidence";
    direction: "ascending" | "descending";
  } | null>(null);

  const handleHistSort = (key: "Date" | "Prediction" | "Confidence") => {
    let direction: "ascending" | "descending" = "ascending";
    if (histSortConfig?.key === key && histSortConfig.direction === "ascending") {
      direction = "descending";
    }
    setHistSortConfig({ key, direction });
  };

  const getHistSortIcon = (key: "Date" | "Prediction" | "Confidence") => {
    if (!histSortConfig || histSortConfig.key !== key) return " ↕";
    return histSortConfig.direction === "ascending" ? " ↑" : " ↓";
  };

  const sortedHistoricalData = [...historicalData].sort((a, b) => {
    if (!histSortConfig) return 0;
    const { key, direction } = histSortConfig;
    let cmp = 0;
    if (key === "Date") {
      cmp = new Date(a.Date).getTime() - new Date(b.Date).getTime();
    } else {
      cmp = a[key] - b[key];
    }
    return direction === "ascending" ? cmp : -cmp;
  });

  const toggleNewsPane = () => {
    setIsNewsPaneVisible(!isNewsPaneVisible);
  };

  return (
    <>
      <div className="statergy-news-split" ref={splitRef}>
        <div className="statergy-pane">
          <div className="statergy-prediction">
            <div className="section-title">
              Nifty Futures O2C Strategy Prediction
              <button className="ns-info-btn" onClick={() => setShowStrategyModal(true)} title="About this strategy">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75"/>
                  <path d="M12 11v5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
                  <circle cx="12" cy="7.5" r="0.75" fill="currentColor"/>
                </svg>
              </button>
            </div>

            {/* Strategy Info Modal */}
            {showStrategyModal && (
              <div className="ns-modal-overlay" onClick={() => setShowStrategyModal(false)}>
                <div className="ns-modal-panel" onClick={e => e.stopPropagation()}>
                  <div className="ns-modal-header">
                    <span className="ns-modal-title">About Our Strategy</span>
                    <button className="ns-modal-close" onClick={() => setShowStrategyModal(false)}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                  <div className="ns-modal-body">
                    <p>{backtestingData["About Strategy"]?.Text || "No data available"}</p>
                  </div>
                </div>
              </div>
            )}
            {hasPredictionImage ? (
              <div className="img">
                <img
                  src={`https://atsstoragefiles.blob.core.windows.net/plots/${today}_prediction_graph_new.jpg`}
                  alt="Prediction Graph"
                  onError={handleImageError}
                />
              </div>
            ) : (
              <div className="no-signal-text" style={{ textAlign: "center" }}>
                No signal generated For today
              </div>
            )}
            {/* <div className="text-summary">
              <div className="heading" onClick={iconHandler}>
                <div className="chevron-icon">{isOpen ? "▲" : "▼"}</div>
                <div className="heading-text">Want to know more about our Strategy?</div>
              </div>
              {showtext && (
                <div
                  className={`text ${
                    showtext ? "text-visible" : "text-hidden"
                  }`}
                  style={{ animation: "fadeIn 0.5s ease-in-out" }}
                >
                  <p
                    className="about-strategy-text"
                    style={{ paddingLeft: "15%" }}
                  >
                    {backtestingData["About Strategy"]?.Text ||
                      "No data available"}
                  </p>
                </div>
              )}
            </div> */}
          </div>

          {/* Tabbed Chart Section — commented out
          <div className="chart-section">
            <div className="chart-tabs">
              <button
                className={`tab-button ${chartActiveTab === "tab1" ? "active" : ""}`}
                onClick={() => { setChartActiveTab("tab1"); setChartLoading(true); }}
              >Price Charts</button>
              <button
                className={`tab-button ${chartActiveTab === "tab2" ? "active" : ""}`}
                onClick={() => { setChartActiveTab("tab2"); setChartLoading(true); }}
              >Volume & OI Chart</button>
            </div>
            <div className="chart-content">
              {chartLoading && <div className="chart-loading">Loading Chart... Please wait</div>}
              {chartActiveTab === "tab1" && (
                <>
                  <iframe src={chartDataUrls["priceChart1"]} title="Price Chart 1" className="chart-iframe" onLoad={() => setChartLoading(false)}></iframe>
                  <iframe src={chartDataUrls["priceChart2"]} title="Price Chart 2" className="chart-iframe" onLoad={() => setChartLoading(false)}></iframe>
                </>
              )}
              {chartActiveTab === "tab2" && (
                <iframe src={chartDataUrls["volumeChart"]} title="Volume Chart" className="chart-iframe" onLoad={() => setChartLoading(false)}></iframe>
              )}
            </div>
          </div>
          */}

          <div className="table-split">
            <Table data={backtestingData} />
            <div className="historical-table">
              <div className="title">Historical Performance</div>
              <div className="table-wrapper">
              <table className="table1 table-striped">
                <thead>
                  <tr>
                    <th onClick={() => handleHistSort("Date")} style={{ cursor: "pointer" }}>
                      Date{getHistSortIcon("Date")}
                    </th>
                    <th onClick={() => handleHistSort("Prediction")} style={{ cursor: "pointer" }}>
                      Prediction{getHistSortIcon("Prediction")}
                    </th>
                    <th onClick={() => handleHistSort("Confidence")} style={{ cursor: "pointer" }}>
                      Confidence{getHistSortIcon("Confidence")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedHistoricalData.map((data, index) => (
                    <tr
                      key={index}
                      style={{
                        backgroundColor:
                          data.Prediction === data.label
                            ? "#D5F3C8"
                            : "#F8D7DA",
                      }}
                    >
                      <td>{data.Date}</td>
                      <td>{data.Prediction}</td>
                      <td>{data.Confidence.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>

        {!isMobile && (
          <div className="ns-drag-handle" onMouseDown={onDragHandleMouseDown} title="Drag to resize" />
        )}

        {isMobile ? (
          <div className="news-container">
            <button className="toggle-button" onClick={toggleNewsPane}>
              {isNewsPaneVisible ? (
                <div>
                  News <ArrowRight style={{ marginLeft: "8px" }} />
                </div>
              ) : (
                <div>
                  News <ArrowLeft style={{ marginLeft: "8px" }} />
                </div>
              )}
            </button>

            <div
              className={`news-pane ${
                isNewsPaneVisible ? "slide-in" : "slide-out"
              }`}
            >
              <div className="news-card-contaier">
                {newsData.Nifty.map((news, index) => (
                  <div key={index} className="news-item">
                    <div className="news-published">{news.published}</div>
                    <div>
                      <b>{news.source.title}</b>
                    </div>
                    <div className="news-title">{news.title.split("-")[0]}</div>
                    <div className="news-source">
                      <a
                        href={news.source.href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          src="assets/images/nifty/arrow.png"
                          alt="arrow-btn"
                        />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="news-pane" style={{ width: newsPaneWidth, minWidth: 280, flexShrink: 0 }}>
            <div className="news-pane-header">
              <div className="title">News</div>
              <div className="news-sort-wrapper">
                <button
                  className="news-sort-btn"
                  onClick={() => setNewsSortDropdownOpen((p) => !p)}
                  title="Sort news"
                >
                  ⇅ {newsSortOrder === "latest" ? "Latest" : "Oldest"}
                </button>
                {newsSortDropdownOpen && (
                  <div className="news-sort-dropdown">
                    <button
                      className={newsSortOrder === "latest" ? "active" : ""}
                      onClick={() => { setNewsSortOrder("latest"); setNewsSortDropdownOpen(false); }}
                    >
                      Latest to Oldest
                    </button>
                    <button
                      className={newsSortOrder === "oldest" ? "active" : ""}
                      onClick={() => { setNewsSortOrder("oldest"); setNewsSortDropdownOpen(false); }}
                    >
                      Oldest to Latest
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="news-card-contaier">
              {[...newsData.Nifty]
                .sort((a, b) => {
                  const tA = new Date(a.published).getTime();
                  const tB = new Date(b.published).getTime();
                  return newsSortOrder === "latest" ? tB - tA : tA - tB;
                })
                .map((news, index) => (
                  <div key={index} className="news-item">
                    <div className="news-published">{news.published}</div>
                    <div><b>{news.source.title}</b></div>
                    <div className="news-title">{news.title.split("-")[0]}</div>
                    <div className="news-source">
                      <a href={news.source.href} target="_blank" rel="noreferrer">
                        <img src="assets/images/nifty/arrow.png" alt="arrow-btn" />
                      </a>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

    </>
  );
};

export default NiftyStatergy;
